// <OrgView> — Org View container (T-051, plan §6.1 / spec §3..§7).
//
// Replaces the legacy `<EmployeesView>` at the `/employees` route.
// Wires:
//   - URL-canonical filter state (useOrgViewFilters)
//   - Single React Query hook against `GET /api/employees?view=org&...`
//     (the envelope route shipped in T-047)
//   - <OrgGridToolbar> at the top (page title + Add Employee CTA +
//     result count + view-mode toggle)
//   - <OrgFiltersSidebar> on the left (320 px sticky)
//   - <OrgCardGrid> on the right (flex-1) — empty state + grid render
//     handled by the grid component
//   - Card click → /employees/:id, Quick edit → ?initialEditing=true,
//     Deactivate → POST /api/employees/:id/{de,re}activate with a
//     window.confirm guard, Send message → mailto: open
//
// Sort dropdown + density toggle land with T-054 pixel polish; the
// view-mode toggle here flips between <OrgCardGrid> (T-051) and
// <OrgTable> (T-052) only. Add Employee modal lands here via T-053.

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { OrgViewEmployee, OrgViewResponse } from '@pa/shared';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { OrgFiltersSidebar } from './OrgFiltersSidebar';
import { OrgGridToolbar, type OrgViewMode } from './OrgGridToolbar';
import { OrgCardGrid } from './OrgCardGrid';
import { OrgTable } from './OrgTable';
import { AddEmployeeModal } from './AddEmployeeModal';
import { sortEmployees, type OrgSortKey } from './OrgSortDropdown';
import { type OrgDensity } from './OrgDensityToggle';
import {
  useOrgViewFilters,
  ORG_VIEW_DEFAULTS,
} from './useOrgViewFilters';
import type { OrgViewerRole } from './OrgEmployeeCardMenu';

const VIEW_MODE_STORAGE_KEY = 'org.viewMode';
const SORT_STORAGE_KEY = 'org.sort';
const DENSITY_STORAGE_KEY = 'org.density';

function readStoredViewMode(): OrgViewMode {
  try {
    const v = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return v === 'table' ? 'table' : 'cards';
  } catch {
    return 'cards';
  }
}

function readStoredSort(): OrgSortKey {
  try {
    const v = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (v === 'startDate' || v === 'team' || v === 'alpha') return v;
  } catch {
    // ignore
  }
  return 'alpha';
}

function readStoredDensity(): OrgDensity {
  try {
    const v = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    if (v === 'compact' || v === 'spacious' || v === 'comfortable') return v;
  } catch {
    // ignore
  }
  return 'comfortable';
}

function buildQueryUrl(filters: ReturnType<typeof useOrgViewFilters>['filters']): string {
  const qs = new URLSearchParams({ view: 'org' });
  if (filters.search) qs.set('search', filters.search);
  if (filters.teamId !== ORG_VIEW_DEFAULTS.teamId) qs.set('teamId', filters.teamId);
  if (filters.siteId !== ORG_VIEW_DEFAULTS.siteId) qs.set('siteId', filters.siteId);
  if (filters.status !== ORG_VIEW_DEFAULTS.status) qs.set('status', filters.status);
  return `/api/employees?${qs.toString()}`;
}

export default function OrgView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const viewerRole = (me?.user.role ?? 'MEMBER') as OrgViewerRole;
  const canAddEmployee = viewerRole === 'OWNER' || viewerRole === 'ADMIN';

  const filtersHook = useOrgViewFilters();
  const { filters, setSearch, setTeamId, setSiteId, setStatus, resetAll } =
    filtersHook;

  const queryUrl = buildQueryUrl(filters);
  const orgQuery = useQuery({
    queryKey: ['org-view', queryUrl],
    queryFn: () => api.get<OrgViewResponse>(queryUrl),
    staleTime: 30_000,
  });

  const [viewMode, setViewModeState] = useState<OrgViewMode>(() =>
    readStoredViewMode(),
  );
  const setViewMode = useCallback((next: OrgViewMode) => {
    setViewModeState(next);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
    } catch {
      // ignore — the choice will fall back to "cards" next session
    }
  }, []);

  const [sort, setSortState] = useState<OrgSortKey>(() => readStoredSort());
  const setSort = useCallback((next: OrgSortKey) => {
    setSortState(next);
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const [density, setDensityState] = useState<OrgDensity>(() =>
    readStoredDensity(),
  );
  const setDensity = useCallback((next: OrgDensity) => {
    setDensityState(next);
    try {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const setStatusMutation = useMutation({
    mutationFn: async ({
      id,
      next,
    }: {
      id: string;
      next: 'inactive' | 'active';
    }) =>
      next === 'inactive'
        ? api.post(`/api/employees/${id}/deactivate`)
        : api.post(`/api/employees/${id}/reactivate`),
    onSuccess: () => {
      // Re-fetch the directory so the row re-paints with the new status.
      queryClient.invalidateQueries({ queryKey: ['org-view'] });
    },
  });

  function onOpenProfile(id: string) {
    navigate(`/employees/${id}`);
  }
  function onQuickEdit(id: string) {
    navigate(`/employees/${id}?initialEditing=true`);
  }
  function onSendMessage(employee: OrgViewEmployee) {
    // Spec §7.2 — open mailto: with the work email. The card type
    // doesn't include workEmail today (server payload in T-047 strips
    // it). Fall back to a non-broken handler for now and log; the
    // server can extend the slim row when this becomes a v1
    // requirement.
    if (typeof window !== 'undefined') {
      // Best-effort placeholder; real mailto wiring depends on
      // workEmail being on the slim row.
      window.location.href = `mailto:?subject=${encodeURIComponent(
        `Message for ${employee.name}`,
      )}`;
    }
  }
  function onDeactivate(employee: OrgViewEmployee) {
    const goingTo: 'inactive' | 'active' =
      employee.status === 'active' ? 'inactive' : 'active';
    const verb = goingTo === 'inactive' ? 'Deactivate' : 'Reactivate';
    if (typeof window !== 'undefined' && !window.confirm(
      `${verb} ${employee.name}? This is reversible.`,
    )) {
      return;
    }
    setStatusMutation.mutate({ id: employee.id, next: goingTo });
  }

  const [addModalOpen, setAddModalOpen] = useState(false);
  function onAddEmployee() {
    setAddModalOpen(true);
  }
  function onAdded() {
    setAddModalOpen(false);
    // The new hire is in PRE_BOARDING — they'll only show up on the
    // Org grid once onboarding completes. Send the user to /onboarding
    // so they can pick up the flow without losing context.
    navigate('/onboarding');
  }

  const rawEmployees = orgQuery.data?.employees ?? [];
  const employees = sortEmployees(rawEmployees, sort);
  const total = orgQuery.data?.total ?? 0;
  const availableTeams = orgQuery.data?.availableTeams ?? [];
  const availableSites = orgQuery.data?.availableSites ?? [];

  return (
    <div className="-m-8" data-testid="org-view">
      <OrgGridToolbar
        total={total}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onAddEmployee={onAddEmployee}
        sort={sort}
        onSortChange={setSort}
        density={density}
        onDensityChange={setDensity}
      />
      <div className="px-10 pb-10 flex gap-8">
        <OrgFiltersSidebar
          filters={filters}
          availableTeams={availableTeams}
          availableSites={availableSites}
          onSearchChange={setSearch}
          onTeamChange={setTeamId}
          onSiteChange={setSiteId}
          onStatusChange={setStatus}
        />
        <div className="flex-1 overflow-y-auto">
          {orgQuery.isLoading && (
            <div
              data-testid="org-view-loading"
              className="text-sm text-slate-400 italic py-8"
            >
              Loading…
            </div>
          )}
          {orgQuery.isError && (
            <div className="bg-white rounded-2xl border border-rose-100 p-6 text-sm text-rose-700">
              Couldn't load the directory. Try refreshing the page.
            </div>
          )}
          {orgQuery.data &&
            (viewMode === 'table' ? (
              employees.length === 0 ? (
                <OrgCardGrid
                  // Empty state owns the layout for both view modes per
                  // spec §6.3 — render the same empty-state block.
                  employees={employees}
                  viewerRole={viewerRole}
                  onOpenProfile={onOpenProfile}
                  onQuickEdit={onQuickEdit}
                  onSendMessage={onSendMessage}
                  onDeactivate={onDeactivate}
                  onClearFilters={resetAll}
                  onAddEmployee={onAddEmployee}
                  canAddEmployee={canAddEmployee}
                />
              ) : (
                <OrgTable
                  employees={employees}
                  viewerRole={viewerRole}
                  onOpenProfile={onOpenProfile}
                  onQuickEdit={onQuickEdit}
                  onSendMessage={onSendMessage}
                  onDeactivate={onDeactivate}
                  sort={sort}
                  onSortChange={setSort}
                />
              )
            ) : (
              <OrgCardGrid
                employees={employees}
                viewerRole={viewerRole}
                onOpenProfile={onOpenProfile}
                onQuickEdit={onQuickEdit}
                onSendMessage={onSendMessage}
                onDeactivate={onDeactivate}
                onClearFilters={resetAll}
                onAddEmployee={onAddEmployee}
                canAddEmployee={canAddEmployee}
                density={density}
              />
            ))}
        </div>
      </div>
      <AddEmployeeModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdded={onAdded}
      />
    </div>
  );
}
